import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Collapse } from '@mui/material'
import { Tooltip } from 'bootstrap'
import DOMPurify from 'dompurify'
import { TextField, Autocomplete, SelectField } from '../../Base/Form'
import { z } from 'zod'
import * as lib from '../../../../js/lib/lib.js'
import { useSelector } from 'react-redux'

const numbersOptional = (dictionary) =>
  z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return undefined
    if (!isNaN(Number(val))) return Number(val)
    return val
  }, z.number({ invalid_type_error: dictionary.flightNumberNotNumberText }).optional())

export const flightDetailsSchema = (dictionary) =>
  z.object({
    airline: z
      .object({
        value: z.string(),
        text: z.string(),
      })
      .optional(),
    flightNumber: numbersOptional(dictionary),
    // flightNumber: z.string().optional().isNumeric(),
  })

const FlightDetails = forwardRef(
  ({ control, errors, setValue, trigger, pendingPayment }, ref) => {
    const { dictionary, airlineOptions, flightList } =
      window.bookingData || window.modifyData

    const [collapseOpen, setCollapseOpen] = useState(true)
    const { hasFlightNumber } = useSelector((state) => state.booking)
    const tooltipRef = useRef(null)

    useImperativeHandle(ref, () => ({
      expand: () => {
        setCollapseOpen(true)
      },
    }))

    const onSelectFlight = (flight) => {
      const { flightNumber, value } = flightList.find((f) => f.value === flight)
      const selectedFlight = airlineOptions.find((a) => a.value === value)

      setValue('flightNumber', flightNumber)
      setValue('airline', selectedFlight)
      trigger('airline')
      trigger('flightNumber')
    }

    const handleCollapseClick = () => {
      if (lib.scrDevices()) {
        setCollapseOpen(!collapseOpen)
      }
    }

    useEffect(() => {
      if (flightList.length > 0) {
        setValue('flightList', flightList[0].value)
        onSelectFlight(flightList[0].value)
      }
    }, [])

    useEffect(() => {
      const handleResize = () => {
        if (!lib.scrDevices()) {
          setCollapseOpen(true)
        }
      }

      window.addEventListener('resize', handleResize)
      handleResize()

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    useEffect(() => {
      if (dictionary.flightDetailsTooltip && tooltipRef.current) {
        const tooltip = new Tooltip(tooltipRef.current, {
          html: true,
        })
        return () => tooltip.dispose()
      }
    }, [dictionary.flightDetailsTooltip])

    return (
      <div className="s-booking-form__form-group">
        <div
          className="row"
          role="button"
          tabIndex={0}
          onClick={handleCollapseClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleCollapseClick()
            }
          }}
          aria-expanded={collapseOpen}
          aria-label="Toggle flight details section">
          <div className="col-lg-6 mb-24 d-flex align-items-center justify-content-between">
            <div className="s-booking-form__form-group__title">
              {dictionary.flightDetailsLabel}
              <div
                ref={tooltipRef}
                className="b-field__info-icon"
                data-bs-toggle="tooltip"
                data-bs-title={DOMPurify.sanitize(
                  dictionary.flightDetailsTooltip
                )}
                onClick={(e) => e.stopPropagation()}>
                <i className="icon icon-info"></i>
              </div>
            </div>
            <div className="d-lg-none ">
              <i
                className={`icon-arrow-down arrow ${
                  collapseOpen ? 'open' : ''
                }`}
              />
            </div>
          </div>
          <div className="col-lg-6 d-flex justify-content-lg-end align-items-end mb-24">
            <span className="s-booking-form__form-group__required">
              {dictionary.flightDetailsRequiredLabel}
            </span>
          </div>
        </div>

        <Collapse in={collapseOpen}>
          {hasFlightNumber && (
            <div className="mb-24">
              <SelectField
                name="flightList"
                label={'Select flight'}
                control={control}
                options={flightList}
                onChange={(e) => onSelectFlight(e.target.value)}
                disabled={pendingPayment}
              />
            </div>
          )}
          <div className="row">
            <div className="col-lg-6 mb-24">
              <Autocomplete
                name={'airline'}
                label={dictionary.flightDetailsAirlineLabel}
                control={control}
                options={airlineOptions}
                getOptionLabel={(option) => option.text}
                error={errors.airline}
                onInputChange={() => {}}
                disabled={hasFlightNumber || pendingPayment}
              />
            </div>
            <div className="col-lg-6 mb-24">
              <TextField
                name="flightNumber"
                label={dictionary.flightDetailsFlightNumberLabel}
                control={control}
                type="text"
                error={errors.flightNumber}
                disabled={hasFlightNumber || pendingPayment}
                maxLength={4}
              />
            </div>
          </div>
        </Collapse>
      </div>
    )
  }
)

FlightDetails.displayName = 'FlightDetails'

export default FlightDetails
